import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';
import { PortalComponent } from './portal/portal.component';
import { AuthModule } from '@app/auth/auth.module';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { environment } from '@env/environment';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CustomMSALInterceptor } from './auth/custom-msal.interceptor';
import { ApmModule, ApmService } from '@elastic/apm-rum-angular';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.MSAL.clientId,
      authority: environment.MSAL.authority,
      redirectUri: environment.MSAL.redirectUri,
      postLogoutRedirectUri: environment.MSAL.logoutUri,
      knownAuthorities: environment.MSAL.knownAuthorities,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);
  protectedResourceMap.set(`${environment.apiUrl}/content`, null);
  protectedResourceMap.set(`${environment.apiUrl}/*`, [
    ...environment.MSAL.scopes,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.MSAL.scopes],
    },
    loginFailedRoute: '/',
  };
}

export function MSALServiceFactory(
  msalInstance: IPublicClientApplication,
  location: Location,
): MsalService {
  return new MsalService(msalInstance, location);
}

export function MSALBroadcastServiceFactory(
  msalInstance: IPublicClientApplication,
  msalService: MsalService,
): MsalBroadcastService {
  return new MsalBroadcastService(msalInstance, msalService);
}

export function MSALGuardFactory(
  guardConfig: MsalGuardConfiguration,
  msalBroadcastService: MsalBroadcastService,
  msalService: MsalService,
  location: Location,
  router: Router,
): MsalGuard {
  return new MsalGuard(
    guardConfig,
    msalBroadcastService,
    msalService,
    location,
    router,
  );
}

@NgModule({
  declarations: [AppComponent, PortalComponent],
  imports: [AppRoutingModule, CoreModule, SharedModule, AuthModule, ApmModule],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomMSALInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: MsalService,
      useFactory: MSALServiceFactory,
      deps: [MSAL_INSTANCE, Location],
    },
    {
      provide: MsalBroadcastService,
      useFactory: MSALBroadcastServiceFactory,
      deps: [MSAL_INSTANCE, MsalService],
    },
    {
      provide: MsalGuard,
      useFactory: MSALGuardFactory,
      deps: [
        MSAL_GUARD_CONFIG,
        MsalBroadcastService,
        MsalService,
        Location,
        Router,
      ],
    },
    ApmService,
  ],
})
export class AppModule {
  constructor(service: ApmService) {
    const apmConfig = environment.elasticApm;
    if (apmConfig?.enabled && apmConfig.serverUrl) {
      service.init({
        serviceName: 'myframeboard-fe',
        serverUrl: apmConfig.serverUrl,
        logLevel: 'debug',
        environment: environment.envName,
        active: apmConfig.enabled,
      });
    }
  }
}
